import { useState, useEffect, useMemo } from 'react';
import { useContracts } from '../../../hooks/useContracts';
import { useGroups } from '../../../hooks/useGroups';
import { ITEMS_PER_PAGE } from '../constants/contractsConstants';

/**
 * Hook para manejar la lógica del diálogo de edición de lista de contratos
 * Este hook se encarga de obtener y paginar los contratos filtrados
 */
export const useContractEditList = ({ contractFilters = {}, open, save, setSave }) => {
  const [filterInputs, setFilterInputs] = useState({
    groupUuid: '',
    dni: '',
    contractNumber: '',
    name: '',
  });
  const [debouncedFilters, setDebouncedFilters] = useState({
    groupUuid: '',
    dni: '',
    contractNumber: '',
    name: '',
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const { data: groups = [] } = useGroups();

  const { data: contracts = [], isLoading } = useContracts(
    contractFilters,
    contractFilters.archived || false
  );

  useEffect(() => {
    setPage(1);
    if (open) {
      const resetFilters = {
        groupUuid: '',
        dni: '',
        contractNumber: '',
        name: '',
      };
      setFilterInputs(resetFilters);
      setDebouncedFilters(resetFilters);
    }
  }, [contractFilters, open]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filterInputs);
      setIsFiltering(false);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [filterInputs]);

  const filteredContracts = useMemo(() => {
    let filtered = contracts;
    const normalize = (value) => (value == null ? '' : String(value)).toLowerCase();
    const groupUuid = debouncedFilters.groupUuid;

    if (groupUuid && groupUuid !== 0) {
      filtered = filtered.filter(
        (contract) => String(contract.uuid_group) === String(groupUuid)
      );
    }

    const searchDni = normalize(debouncedFilters.dni).trim();
    if (searchDni) {
      filtered = filtered.filter((contract) => {
        if (!contract.subscribers || contract.subscribers.length === 0) return false;
        return contract.subscribers.some((subscriber) =>
          normalize(subscriber.subscriber?.dni).includes(searchDni)
        );
      });
    }

    const searchNumber = normalize(debouncedFilters.contractNumber).trim();
    if (searchNumber) {
      filtered = filtered.filter((contract) =>
        normalize(contract.contract_number).includes(searchNumber)
      );
    }

    const searchName = normalize(debouncedFilters.name).trim();
    if (searchName) {
      filtered = filtered.filter((contract) => {
        if (!contract.subscribers || contract.subscribers.length === 0) return false;
        return contract.subscribers.some(
          (subscriber) =>
            normalize(subscriber.subscriber?.first_name).includes(searchName) ||
            normalize(subscriber.subscriber?.last_name).includes(searchName)
        );
      });
    }

    return filtered;
  }, [contracts, debouncedFilters]);

  const displayedContracts = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredContracts.slice(startIndex, endIndex);
  }, [filteredContracts, page, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / rowsPerPage));
  const totalCount = filteredContracts.length;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleFilterChange = (name, value) => {
    setIsFiltering(true);
    setFilterInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedContract(null);
  };

  const handleSaveContract = () => {
    setSave(!save);
    setEditDialogOpen(false);
    setSelectedContract(null);
  };

  return {
    displayedContracts,
    isLoading,

    filterInputs,
    page,
    totalPages,
    totalCount,
    rowsPerPage,

    groups,
    isFiltering,

    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleEdit,

    editDialogOpen,
    selectedContract,
    handleCloseEditDialog,
    handleSaveContract,
  };
};
